// What the sidebar's "Check Google Ads for campaigns" button tells a person (#6612). Pure, shared by the toolbar.
import type { ExternalCampaignDiscoveryResponse } from '@/lib/types';

/** The sidebar filter choices that decide whether a newly listed campaign shows. */
export interface SidebarFilterChoices {
  types: Set<string>;
  statuses: Set<string>;
  platforms: Set<string>;
  /** Whether the Platform section is offered; a saved platform choice the person cannot see is not named. */
  showPlatforms: boolean;
}

/**
 * The filter options, by label, that hide a newly listed campaign from the sidebar.
 *
 * A listed campaign is always an External, Live, Google campaign: discovery only lists enabled Google Ads campaigns.
 */
export function filtersHidingListedCampaigns(filters: SidebarFilterChoices): string[] {
  const googleHidden = filters.showPlatforms && filters.platforms.size === 1 && filters.platforms.has('meta');
  return [
    ...(googleHidden ? ['Google'] : []),
    ...(filters.statuses.has('live') ? [] : ['Live']),
    ...(filters.types.has('external') ? [] : ['External']),
  ];
}

/** The message shown after a check, naming any filter that hides what it found. */
export function campaignCheckMessage(result: ExternalCampaignDiscoveryResponse, filters: SidebarFilterChoices): string {
  if (result.already_running) return 'A check of this Google Ads account is already running. Try again in a minute.';
  if (result.discovered === 0) return 'No new campaigns found in Google Ads.';

  const found =
    result.discovered === 1
      ? '1 new campaign found in Google Ads.'
      : `${result.discovered} new campaigns found in Google Ads.`;
  const hiding = filtersHidingListedCampaigns(filters);
  if (hiding.length === 0) return found;
  const labels = hiding.length === 1 ? hiding[0] : `${hiding.slice(0, -1).join(', ')} and ${hiding[hiding.length - 1]}`;
  return `${found} Turn on ${labels} in the filter to see ${result.discovered === 1 ? 'it' : 'them'}.`;
}
